import React, { useState } from "react"
import { useMutation } from "urql"

import PaymentProviderCardForm from "../../components/payments/PaymentProviderCardForm"
import PaymentProviderWrapper, { usePaymentProvider } from "../../components/payments/PaymentProviderWrapper"
import { Button } from "../../components/shared/Buttons"
import { Banner } from "../../components/shared/Layout"
import Typography from "../../components/shared/Typography"
import { canProcessRegistrationPayment } from "../BookableEvents/bookableEventPaymentStatus"
import {
  PROCESS_REGISTRATION_PAYMENT_MUTATION,
  PROCESS_SERIES_REGISTRATION_PAYMENT_MUTATION
} from "../BookableEvents/mutations"

function PayNowForm({ practice, registration, isSeries, accentColor, onPaymentSuccess }) {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { handleCardSavedOnProvider } = usePaymentProvider()

  const mutation = isSeries ? PROCESS_SERIES_REGISTRATION_PAYMENT_MUTATION : PROCESS_REGISTRATION_PAYMENT_MUTATION
  const fieldName = isSeries ? "processSeriesRegistrationPayment" : "processRegistrationPayment"
  const [, processPayment] = useMutation(mutation)

  const formatCurrency = (amountCents) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amountCents / 100)

  const handlePayNow = async () => {
    setProcessing(true)
    setError(null)

    try {
      const cardResult = await handleCardSavedOnProvider()
      if (cardResult?.error) {
        setError(cardResult.error)
        return
      }

      const result = await processPayment({ registrationId: registration.id })

      if (result.data?.[fieldName]?.result === "success") {
        onPaymentSuccess()
      } else {
        setError(result.data?.[fieldName]?.errors?.[0] || "Payment could not be processed. Please try again.")
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred")
    } finally {
      setProcessing(false)
    }
  }

  if (!practice?.hasPaymentProvider) {
    return (
      <Banner type="warning">
        Online payment is not available for this practice. Please contact them to complete payment.
      </Banner>
    )
  }

  return (
    <div className="rounded-lg border border-gray-light p-4">
      <Typography variant="subtitle" className="mb-2 font-semibold">
        Complete your payment
      </Typography>
      <Typography variant="body" className="mb-4 text-gray-dark">
        Amount due: <strong>{formatCurrency(registration.amountCents)}</strong>
      </Typography>

      <PaymentProviderCardForm practice={practice} />

      {error && (
        <Banner type="error" className="mt-4">
          {error}
        </Banner>
      )}

      <Button
        type="primary"
        className="mt-4 w-full"
        onClick={handlePayNow}
        disabled={processing}
        style={{ backgroundColor: accentColor }}>
        {processing ? "Processing..." : "Pay now"}
      </Button>
    </div>
  )
}

export default function EventRegistrationPayNowSection({
  practice,
  client,
  registration,
  isSeries = false,
  accentColor,
  onPaymentSuccess
}) {
  if (!canProcessRegistrationPayment(registration)) return null

  return (
    <PaymentProviderWrapper practice={practice} client={client}>
      <PayNowForm
        practice={practice}
        registration={registration}
        isSeries={isSeries}
        accentColor={accentColor}
        onPaymentSuccess={onPaymentSuccess}
      />
    </PaymentProviderWrapper>
  )
}
